import React, { useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { AudioScrubBar } from "./AudioScrubBar";

interface VoicePreviewPlayerProps {
  src: string;
  /** known clip length (ms) from the recorder — used until <audio> metadata loads */
  durationMs?: number;
  className?: string;
}

function fmt(seconds: number): string {
  const s = isFinite(seconds) && seconds > 0 ? seconds : 0;
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

// brutalist-styled mini audio player for the just-recorded clip preview. replaces the raw
// <audio controls> default so the recorder matches the chat's visual language: a circular
// play/pause button, a scrubbable progress track, and a live time readout.
export function VoicePreviewPlayer({ src, durationMs, className }: VoicePreviewPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  // webm blobs often report duration=Infinity until seeked, so seed from the recorder's count
  const [duration, setDuration] = useState((durationMs ?? 0) / 1000);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => undefined);
    else el.pause();
  };

  const seek = (ratio: number) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    el.currentTime = ratio * duration;
    setCurrent(el.currentTime);
  };

  const progress = duration > 0 ? current / duration : 0;

  return (
    <div className={`flex items-center gap-2 min-w-0 ${className ?? ""}`}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (isFinite(d) && d > 0) setDuration(d);
        }}
      />
      <button
        type="button"
        onClick={toggle}
        className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-ink shrink-0 bg-accent/30 hover:bg-accent/50"
        aria-label={playing ? "pause preview" : "play preview"}
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <AudioScrubBar progress={progress} onSeek={seek} className="flex-1 min-w-0" />
      <span className="text-[10px] text-gray-500 shrink-0 tabular-nums">
        {fmt(current)}/{fmt(duration)}
      </span>
    </div>
  );
}
